// ----------------------------------------------------------------------------
// Component: Accordion
// ----------------------------------------------------------------------------
//
// Generic accordion component.
//
// Requires the following structure:
// .js-accordion__item
//     .js-accordion__button
//     .js-accordion__content
//
// When the button is clicked, 'isOpen' class is added/removed for all three
// elements.
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Styles
import './accordion.scss';

// -------------------------------------
// Variables
// -------------------------------------

var ACCORDION = '.js-accordion';
var ACCORDION_BUTTON = '.js-accordion__button';
var ACCORDION_ITEM = '.js-accordion__item';
var ACCORDION_CONTENT = '.js-accordion__content';
var ACCORDION_TITLE = '.accordion__title';
var ACCORDION_CONTENT_MOBILE = '.js-accordion__content--mobile-only';

// Constant trigger classes
var OPEN_CLASS = 'is-open';

// -------------------------------------
// Helper Functions
// -------------------------------------

function setTabIndex(e, value) {
  e.setAttribute('tabindex', value);
}
function closeAccordion(accordion) {
  accordion.button.classList.remove(OPEN_CLASS);
  accordion.item.classList.remove(OPEN_CLASS);
  accordion.content.classList.remove(OPEN_CLASS);
  accordion.content.hidden = true;
  accordion.content.style.height = null;
  accordion.button.setAttribute('aria-expanded', 'false');
  accordion.content.querySelectorAll('a').forEach(function (el) {
    setTabIndex(el, -1);
  });
}
function openAccordion(accordion) {
  var heightOffset = parseFloat(window.getComputedStyle(accordion.content).getPropertyValue('--accordion-content-height-offset')) || 0;
  accordion.button.classList.add(OPEN_CLASS);
  accordion.item.classList.add(OPEN_CLASS);
  accordion.content.classList.add(OPEN_CLASS);
  accordion.content.style.height = accordion.content.scrollHeight + heightOffset + 'px';
  accordion.button.setAttribute('aria-expanded', 'true');
  accordion.content.hidden = false;
  accordion.content.querySelectorAll('a').forEach(function (el) {
    setTabIndex(el, 0);
  });
}
function toggleAccordion(accordion) {
  if (accordion.isOpening && accordion.container.hasAttribute('data-collapse-all')) {
    accordion.container.querySelectorAll(ACCORDION_ITEM).forEach(function (item) {
      var button = item.querySelector(ACCORDION_BUTTON);
      var content = item.querySelector(ACCORDION_CONTENT);
      if (button && content) {
        closeAccordion({
          button,
          item,
          content
        });
      }
    });
  }
  if (accordion.isOpening) {
    openAccordion(accordion);
  } else {
    closeAccordion(accordion);
  }
}
function accordionEventHandle(e) {
  var button = e.target.closest(ACCORDION_BUTTON);
  var item = button.closest(ACCORDION_ITEM);
  var title = button.closest(ACCORDION_TITLE);
  var container = button.closest(ACCORDION);

  // Store elements as an object
  var accordion = {
    button: button,
    item: item,
    title: title,
    content: title.nextElementSibling,
    isOpening: !button.classList.contains(OPEN_CLASS),
    container: container,
    button_group: container.querySelectorAll(ACCORDION_BUTTON)
  };

  // Collapse any open sibling & children accordions if data attribute set to true
  if (e.type === 'click') {
    e.preventDefault();
    toggleAccordion(accordion);
  } else if (e.type === 'keydown') {
    var key = e.which.toString();
    if (key.match(/35|36|38|40/)) {
      e.preventDefault();
      var index = Array.prototype.indexOf.call(accordion.button_group, button);
      var length = accordion.button_group.length;
      var newIndex;
      switch (key) {
        case '38':
          // Up
          newIndex = (index + length + -1) % length;
          accordion.button_group[newIndex].focus();
          break;
        case '40':
          // Down
          newIndex = (index + length + 1) % length;
          accordion.button_group[newIndex].focus();
          break;
        case '35':
          // End
          accordion.button_group[length - 1].focus();
          break;
        case '36':
          // Home
          accordion.button_group[0].focus();
          break;
      }
    }
  }
}
function syncAccordionLinks(content) {
  var value = content.classList.contains(OPEN_CLASS) ? 0 : -1;
  content.querySelectorAll('a').forEach(function (e) {
    setTabIndex(e, value);
  });
}
function openAccordionFromHash() {
  var hash = window.location.hash;
  if (hash.length <= 1) {
    return;
  }
  var targetId;
  try {
    targetId = decodeURIComponent(hash.slice(1));
  } catch (_unused) {
    return;
  }
  var target = document.getElementById(targetId);
  if (!target || !target.matches(ACCORDION_ITEM)) {
    return;
  }
  var button = target.querySelector(ACCORDION_BUTTON);
  var content = target.querySelector(ACCORDION_CONTENT);
  var container = target.closest(ACCORDION);
  if (!button || !content || !container) {
    return;
  }
  toggleAccordion({
    button: button,
    item: target,
    content: content,
    container: container,
    isOpening: true
  });
}

// -------------------------------------
// Hooks
// -------------------------------------

document.addEventListener('DOMContentLoaded', function () {
  var accordionButtons = document.querySelectorAll(ACCORDION_BUTTON);
  document.querySelectorAll(ACCORDION_CONTENT).forEach(function (e) {
    syncAccordionLinks(e);
  });
  accordionButtons.forEach(function (e) {
    e.addEventListener('click', accordionEventHandle);
    e.addEventListener('keydown', accordionEventHandle);
  });
  openAccordionFromHash();
});
window.addEventListener('hashchange', openAccordionFromHash);
window.addEventListener('resize', function () {
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 1024) {
    // On mobile, find all accordion content links of the close accordions to add tabindex -1
    var accordionContentLinks = document.querySelectorAll("".concat(ACCORDION_CONTENT, ":not(.").concat(OPEN_CLASS, ") a"));
    accordionContentLinks.forEach(function (e) {
      setTabIndex(e, -1);
    });
  } else {
    // If on desktop, find all accordion content links that belongs to the #accordion-cart--content on the cart page to remove tabindex -1.
    var accordionCartContentLinks = document.querySelectorAll("".concat(ACCORDION_CONTENT).concat(ACCORDION_CONTENT_MOBILE, " a"));
    accordionCartContentLinks.forEach(function (e) {
      setTabIndex(e, 0);
    });
  }

  // Re-adjust all the expanded accordion content tab height on "resize" event by setting their height to "auto"
  var expandedAccordionContent = document.querySelectorAll("".concat(ACCORDION_CONTENT, ".").concat(OPEN_CLASS));
  expandedAccordionContent.forEach(function (accordionContent) {
    accordionContent.style.height = 'auto';
  });
});